import type { ReactElement } from "react"
import type { BlockStyleOverrides } from "@/components/website-builder/blocks/types"
import { ColumnGrid } from "@/components/website-builder/ColumnGrid"
import { resolveItemStyle, wrapWithBackgroundIfNeeded, type BlockComponentProps } from "./shared"

export function TeamMembersBlock({ content, resolved }: BlockComponentProps) {
  const tmHeading = (content?.heading as string) ?? "Meet our team"
  const tmMembers = (content?.members as Array<{
    name?: string
    role?: string
    bio?: string
    image_url?: string
    email?: string
    linkedin?: string
  }>) ?? []
  const tmLayout = (content?.layout as "grid" | "list") ?? "grid"
  const tmColumns = Math.min(6, Math.max(3, (content?.columns as number) ?? 3))
  const primaryColor = "var(--nos-color-primary, #3b82f6)"
  // #RRGGBB20-style alpha suffix can't apply to a var(); color-mix reproduces it exactly (0x20/255 ≈ 12.5%)
  const primaryTint = `color-mix(in srgb, ${primaryColor} 12.5%, transparent)`
  const tmSectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-8"} ${resolved.cornerClass} ${resolved.alignClass}`.trim()
    : "px-4 py-8 @sm:px-6 @md:px-8"
  return wrapWithBackgroundIfNeeded(
    content,
    <section className={tmSectionClass} style={resolved.sectionStyle}>
      <h2
        className="text-2xl font-bold mb-6 text-center"
        style={{ color: primaryColor }}
      >
        {tmHeading}
      </h2>
      {tmMembers.length === 0 ? (
        <p className="text-center text-muted-foreground text-sm">
          No team members yet.
        </p>
      ) : tmLayout === "list" ? (
        <div className="space-y-6 max-w-2xl mx-auto">
          {tmMembers.map((m, i) => (
            <div
              key={i}
              className="flex flex-col @sm:flex-row gap-4 items-start p-4 rounded-lg border bg-card"
            >
              {m.image_url ? (
                <img
                  src={m.image_url}
                  alt={(m.name && m.name.trim()) ? `Photo of ${m.name}` : "Team member"}
                  className="h-24 w-24 rounded-full object-cover flex-shrink-0"
                  style={{ borderColor: primaryColor, borderWidth: 2, borderStyle: "solid" }}
                />
              ) : (
                <div
                  className="h-24 w-24 rounded-full flex items-center justify-center text-2xl font-semibold flex-shrink-0"
                  style={{ background: primaryTint, color: primaryColor }}
                >
                  {((m.name ?? "?")[0] ?? "?").toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg">{(m.name && m.name.trim()) || "Unknown"}</h3>
                {m.role && (
                  <p className="text-sm font-medium" style={{ color: primaryColor }}>
                    {m.role}
                  </p>
                )}
                {m.bio && (
                  <p className="mt-2 text-sm text-muted-foreground">{m.bio}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-3">
                  {m.email && (
                    <a
                      href={`mailto:${m.email}`}
                      className="text-sm font-medium hover:underline"
                      style={{ color: primaryColor }}
                    >
                      {m.email}
                    </a>
                  )}
                  {m.linkedin && (
                    <a
                      href={m.linkedin.startsWith("http") ? m.linkedin : `https://${m.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium hover:underline"
                      style={{ color: primaryColor }}
                    >
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          <ColumnGrid columns={tmColumns} itemCount={tmMembers.length}>
            {tmMembers.map((m, i) => {
              const memberItemStyle = resolveItemStyle((m as Record<string, unknown>)._styles as BlockStyleOverrides | undefined)
              return (
              <div
                key={i}
                className="flex flex-col items-center rounded-xl bg-card border p-6 text-center"
                style={memberItemStyle}
              >
                {m.image_url ? (
                  <img
                    src={m.image_url}
                    alt={(m.name && m.name.trim()) ? `Photo of ${m.name}` : "Team member"}
                    className="h-24 w-24 rounded-full object-cover border-2"
                    style={{ borderColor: primaryColor }}
                  />
                ) : (
                  <div
                    className="h-24 w-24 rounded-full flex items-center justify-center text-2xl font-semibold"
                    style={{ background: primaryTint, color: primaryColor }}
                  >
                    {((m.name ?? "?")[0] ?? "?").toUpperCase()}
                  </div>
                )}
                <h3 className="mt-4 font-semibold">{(m.name && m.name.trim()) || "Unknown"}</h3>
                {m.role && (
                  <p className="text-sm font-medium" style={{ color: primaryColor }}>
                    {m.role}
                  </p>
                )}
                {m.bio && (
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {m.bio}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-2 justify-center">
                  {m.email && (
                    <a
                      href={`mailto:${m.email}`}
                      className="text-xs font-medium hover:underline"
                      style={{ color: primaryColor }}
                    >
                      Email
                    </a>
                  )}
                  {m.linkedin && (
                    <a
                      href={m.linkedin.startsWith("http") ? m.linkedin : `https://${m.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium hover:underline"
                      style={{ color: primaryColor }}
                    >
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
              )
            })}
          </ColumnGrid>
        </div>
      )}
    </section>
  ) as ReactElement
}
